import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { meta, STATE_LABELS } from '@/lib/lifecycle';
import { CAL } from '@/constants/testIds';
import {
  X, Send, Phone, Mail, MessageSquare, FileText, Clipboard,
  History, Trash2, RefreshCw, Loader2, Star, Home, Video,
  AlertTriangle, Plus, ArrowRight, CheckCircle2,
} from 'lucide-react';

const STATE_ORDER = [
  'created','assigned','reminder_sent','confirmed','en_route','arrived',
  'consultation_started','consultation_completed','quotation_generated',
  'device_recommended','sale_completed','followup_scheduled','closed',
];
const EXCEPTION_STATES = ['no_show', 'cancelled', 'rescheduled'];

export default function AppointmentDrawer({
  appointmentId, lookups, clinics, employees, rooms, aptTypes, onClose, onChanged,
}) {
  const [apt, setApt] = useState(null);
  const [history, setHistory] = useState([]);
  const [allowed, setAllowed] = useState({ current: null, allowed_next: [] });
  const [tasks, setTasks] = useState([]);
  const [conversation, setConversation] = useState({ messages: [] });
  const [commStatus, setCommStatus] = useState({ whatsapp: { enabled: false } });
  const [loading, setLoading] = useState(true);
  const [savingTransition, setSavingTransition] = useState(null);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [patient, setPatient] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, h, allow, t, cs] = await Promise.all([
        api.get(`/appointments/${appointmentId}`),
        api.get(`/appointments/${appointmentId}/history`),
        api.get(`/appointments/${appointmentId}/allowed-transitions`),
        api.get(`/tasks`, { params: { appointment_id: appointmentId } }),
        api.get(`/communication/status`),
      ]);
      setApt(a.data);
      api.get(`/patients/${a.data.patient_id}`).then((r) => setPatient(r.data)).catch(() => setPatient(null));
      setHistory(h.data);
      setAllowed(allow.data);
      setTasks(t.data);
      setCommStatus(cs.data);
      setNotes(a.data.notes || '');
      // load conversation
      const conv = await api.get(`/communication/conversations/${a.data.patient_id}`);
      setConversation(conv.data);
    } catch (err) {
      toast.error('Failed to load appointment');
    } finally {
      setLoading(false);
    }
  }, [appointmentId]);

  useEffect(() => { load(); }, [load]);

  if (loading || !apt) {
    return (
      <aside className="w-[380px] shrink-0 border-l border-stone-200 bg-white p-4 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
      </aside>
    );
  }

  const fallbackPatient = lookups.patient[apt.patient_id] || { full_name: apt.patient_name, mobile: apt.patient_mobile };
  const p = patient || fallbackPatient;
  const aptType = lookups.aptType[apt.appointment_type_id];
  const clinic = lookups.clinic[apt.clinic_id];
  const employee = lookups.employee[apt.audiologist_id];
  const room = lookups.room[apt.room_id];
  const m = meta(apt.state);
  const Icon = m.icon;

  const transition = async (to_state, reason = null, override = false) => {
    setSavingTransition(to_state);
    try {
      await api.post(`/appointments/${apt.id}/transition`, { to_state, reason, override });
      toast.success(`→ ${STATE_LABELS[to_state]}`);
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Transition failed');
    } finally {
      setSavingTransition(null);
    }
  };

  const saveNotes = async () => {
    try {
      await api.patch(`/appointments/${apt.id}`, { notes });
      toast.success('Notes saved');
      onChanged && onChanged();
    } catch { toast.error('Failed to save notes'); }
  };

  const createTask = async () => {
    if (!newTaskTitle.trim()) return;
    try {
      await api.post('/tasks', {
        title: newTaskTitle.trim(),
        appointment_id: apt.id,
        patient_id: apt.patient_id,
        status: 'open',
      });
      setNewTaskTitle('');
      const t = await api.get('/tasks', { params: { appointment_id: apt.id } });
      setTasks(t.data);
    } catch { toast.error('Failed to create task'); }
  };

  const toggleTask = async (task) => {
    const next = task.status === 'done' ? 'open' : 'done';
    try {
      await api.patch(`/tasks/${task.id}`, { ...task, status: next });
      const t = await api.get('/tasks', { params: { appointment_id: apt.id } });
      setTasks(t.data);
    } catch { toast.error('Failed to update task'); }
  };

  const sendWhatsApp = async (templateId) => {
    try {
      const { data } = await api.post('/communication/whatsapp/send', {
        patient_id: apt.patient_id,
        appointment_id: apt.id,
        template_id: templateId,
        body: `${templateId} — ${aptType?.name || ''} on ${new Date(apt.start_ts).toLocaleString()}`,
        parameters: {
          patient_name: p?.full_name || '',
          appointment_time: new Date(apt.start_ts).toLocaleString(),
          clinic: clinic?.name || '',
        },
      });
      if (data.status === 'disabled') {
        toast.info('WhatsApp is not activated — message logged as draft.');
      } else if (data.status === 'sent') {
        toast.success('WhatsApp sent');
      } else {
        toast.warning(`Message ${data.status}`);
      }
      const conv = await api.get(`/communication/conversations/${apt.patient_id}`);
      setConversation(conv.data);
    } catch (err) {
      toast.error('Failed to send WhatsApp');
    }
  };

  const cancel = async () => {
    if (!window.confirm('Cancel this appointment?')) return;
    try {
      await api.delete(`/appointments/${apt.id}`, { params: { reason: 'Cancelled by user' } });
      toast.success('Appointment cancelled');
      onChanged && onChanged();
      onClose();
    } catch { toast.error('Failed to cancel'); }
  };

  const reassign = async (newEmpId) => {
    try {
      await api.patch(`/appointments/${apt.id}?override=false`, { audiologist_id: newEmpId });
      toast.success('Reassigned');
      await load();
      onChanged && onChanged();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.message === 'Conflict detected') {
        if (window.confirm('Conflict detected. Override?')) {
          await api.patch(`/appointments/${apt.id}?override=true`, { audiologist_id: newEmpId });
          toast.success('Reassigned (override)');
          await load();
          onChanged && onChanged();
        }
      } else {
        toast.error('Failed to reassign');
      }
    }
  };

  return (
    <aside
      className="w-[400px] shrink-0 border-l border-stone-200 bg-white flex flex-col shadow-xl"
      data-testid={CAL.drawer}
    >
      {/* Header */}
      <div className="p-3 border-b border-stone-200 flex items-start gap-2">
        <div
          className="w-1 self-stretch rounded-full"
          style={{ backgroundColor: m.text }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <Icon className="w-4 h-4" style={{ color: m.text }} />
            <span
              className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded"
              style={{ backgroundColor: m.bg, color: m.text, border: `1px solid ${m.border}` }}
            >
              {m.label}
            </span>
            {apt.priority === 'vip' && (
              <Badge className="bg-black text-white text-[9px] px-1"><Star className="w-2.5 h-2.5 mr-0.5 fill-current" />VIP</Badge>
            )}
            {apt.priority === 'emergency' && (
              <Badge className="bg-red-600 text-white text-[9px] px-1"><AlertTriangle className="w-2.5 h-2.5 mr-0.5" />EMERGENCY</Badge>
            )}
            {apt.channel === 'home_visit' && (
              <Badge variant="outline" className="text-[9px] border-dashed"><Home className="w-2.5 h-2.5 mr-0.5" />Home</Badge>
            )}
            {apt.channel === 'teleconsultation' && (
              <Badge variant="outline" className="text-[9px] border-dotted"><Video className="w-2.5 h-2.5 mr-0.5" />Tele</Badge>
            )}
          </div>
          <div className="font-heading text-base font-bold text-stone-900 truncate mt-1">
            {p?.full_name || 'Patient'}
          </div>
          <div className="text-xs text-stone-500 truncate">
            {aptType?.name || '—'} · {new Date(apt.start_ts).toLocaleString(undefined, {
              weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
            })}
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} className="h-7 w-7" data-testid={CAL.drawerClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <Tabs defaultValue="summary" className="flex-1 flex flex-col overflow-hidden">
        <TabsList className="grid grid-cols-5 mx-3 mt-2 h-8 shrink-0">
          <TabsTrigger value="summary" className="text-[10px] uppercase tracking-wider" data-testid={CAL.drawerTab('summary')}>Summary</TabsTrigger>
          <TabsTrigger value="lifecycle" className="text-[10px] uppercase tracking-wider" data-testid={CAL.drawerTab('lifecycle')}>Lifecycle</TabsTrigger>
          <TabsTrigger value="tasks" className="text-[10px] uppercase tracking-wider" data-testid={CAL.drawerTab('tasks')}>Tasks</TabsTrigger>
          <TabsTrigger value="comm" className="text-[10px] uppercase tracking-wider" data-testid={CAL.drawerTab('communication')}>Comm</TabsTrigger>
          <TabsTrigger value="audit" className="text-[10px] uppercase tracking-wider" data-testid={CAL.drawerTab('audit')}>Audit</TabsTrigger>
        </TabsList>

        <ScrollArea className="flex-1 hc-scrollbar">
          {/* Summary tab */}
          <TabsContent value="summary" className="p-3 space-y-3 mt-0">
            <InfoRow label="Patient" value={
              <div>
                <div className="font-medium">{p?.full_name}</div>
                <div className="text-xs text-stone-500">
                  {p?.mobile} · {p?.age ? `${p.age}y` : '—'} · {p?.gender || '—'}
                </div>
                <div className="text-xs text-stone-500">
                  Lead: {p?.lead_source || '—'} · {p?.hearing_loss_classification || '—'}
                </div>
              </div>
            } />
            <InfoRow label="Clinic" value={clinic?.name || '—'} />
            <InfoRow label="Room" value={room?.name || '—'} />
            <InfoRow label="Audiologist" value={
              <Select value={apt.audiologist_id || ''} onValueChange={reassign}>
                <SelectTrigger className="h-8" data-testid={CAL.drawerAction('reassign-select')}>
                  <SelectValue placeholder="Assign audiologist" />
                </SelectTrigger>
                <SelectContent>
                  {employees.filter(e => e.role === 'audiologist').map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            } />
            <InfoRow label="Duration" value={`${apt.duration_minutes} min`} />
            <InfoRow label="Journey stage" value={p?.journey_stage || '—'} />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1">Internal Notes</div>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="text-sm resize-none"
                placeholder="Add operational notes…"
                data-testid="drawer-notes-input"
              />
              <div className="flex justify-end mt-1">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={saveNotes} data-testid="drawer-save-notes">
                  Save Notes
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Lifecycle tab */}
          <TabsContent value="lifecycle" className="p-3 space-y-3 mt-0">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1.5">
                Current State
              </div>
              <div className="flex items-center gap-1.5 mb-3">
                <Icon className="w-4 h-4" style={{ color: m.text }} />
                <span className="font-heading font-semibold text-stone-900">{m.label}</span>
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1.5">
                Next Actions
              </div>
              <div className="flex flex-col gap-1.5">
                {allowed.allowed_next.length === 0 && (
                  <div className="text-xs text-stone-400 italic">Terminal state — no transitions allowed.</div>
                )}
                {allowed.allowed_next.map((s) => {
                  const sm = meta(s);
                  const SIcon = sm.icon;
                  const isException = EXCEPTION_STATES.includes(s);
                  return (
                    <Button
                      key={s}
                      variant="outline"
                      size="sm"
                      disabled={savingTransition === s}
                      onClick={() => transition(s)}
                      data-testid={CAL.transitionButton(s)}
                      className="justify-start h-9 border-stone-300"
                      style={isException ? { borderColor: sm.border, color: sm.text } : {}}
                    >
                      {savingTransition === s ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <SIcon className="w-4 h-4 mr-2" style={{ color: sm.text }} />
                      )}
                      <ArrowRight className="w-3 h-3 mr-2 text-stone-400" />
                      {sm.label}
                    </Button>
                  );
                })}
              </div>
            </div>

            <div className="border-t border-stone-200 pt-3">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1.5">
                Full lifecycle
              </div>
              <ol className="space-y-1">
                {STATE_ORDER.map((s) => {
                  const sm = meta(s);
                  const done = STATE_ORDER.indexOf(s) < STATE_ORDER.indexOf(apt.state);
                  const current = s === apt.state;
                  return (
                    <li key={s} className="flex items-center gap-2 text-xs">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                        current ? 'border-stone-900 bg-stone-900' : done ? 'border-emerald-500 bg-emerald-500' : 'border-stone-300 bg-white'
                      }`}>
                        {done && <CheckCircle2 className="w-3 h-3 text-white" />}
                      </div>
                      <span className={current ? 'font-semibold text-stone-900' : done ? 'text-stone-600' : 'text-stone-400'}>
                        {sm.label}
                      </span>
                    </li>
                  );
                })}
              </ol>
            </div>

            <div className="border-t border-stone-200 pt-3">
              <Button
                variant="outline"
                size="sm"
                className="w-full text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                onClick={cancel}
                data-testid={CAL.drawerAction('cancel')}
              >
                <Trash2 className="w-4 h-4 mr-2" /> Cancel appointment
              </Button>
            </div>
          </TabsContent>

          {/* Tasks tab */}
          <TabsContent value="tasks" className="p-3 space-y-2 mt-0">
            <div className="flex gap-2">
              <Input
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                placeholder="Add a task…"
                className="h-8 text-sm"
                data-testid="drawer-new-task-input"
              />
              <Button size="sm" onClick={createTask} className="h-8 bg-stone-900 hover:bg-stone-800 text-white" data-testid="drawer-add-task">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {tasks.length === 0 ? (
              <div className="text-xs text-stone-400 italic mt-2">No tasks yet.</div>
            ) : (
              <div className="space-y-1 mt-2">
                {tasks.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => toggleTask(t)}
                    className={`w-full text-left border border-stone-200 rounded px-2 py-1.5 hover:bg-stone-50 flex items-center gap-2 ${
                      t.status === 'done' ? 'opacity-60 line-through' : ''
                    }`}
                    data-testid={`task-${t.id}`}
                  >
                    <div className={`w-4 h-4 rounded border ${t.status === 'done' ? 'bg-emerald-500 border-emerald-500' : 'border-stone-300'}`}>
                      {t.status === 'done' && <CheckCircle2 className="w-4 h-4 text-white" />}
                    </div>
                    <span className="text-sm text-stone-800">{t.title}</span>
                  </button>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Communication tab */}
          <TabsContent value="comm" className="p-3 space-y-3 mt-0">
            <div className="p-2 rounded bg-stone-50 border border-stone-200 text-xs">
              <div className="flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5" />
                <span className="font-semibold">WhatsApp</span>
                {commStatus.whatsapp?.enabled ? (
                  <Badge className="bg-emerald-100 text-emerald-800 text-[9px]">Active</Badge>
                ) : (
                  <Badge variant="outline" className="text-[9px]">Not activated</Badge>
                )}
              </div>
              {!commStatus.whatsapp?.enabled && (
                <div className="text-stone-500 mt-1">Add <code>KARIX_API_KEY</code> to backend .env to activate.</div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-1.5">
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => sendWhatsApp('appointment_confirm')} data-testid={CAL.drawerAction('wa-confirm')}>
                <Send className="w-3 h-3 mr-1" /> Send Confirmation
              </Button>
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => sendWhatsApp('appointment_reminder')} data-testid={CAL.drawerAction('wa-reminder')}>
                <Send className="w-3 h-3 mr-1" /> Send Reminder
              </Button>
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => sendWhatsApp('preparation_instructions')}>
                <FileText className="w-3 h-3 mr-1" /> Prep Instructions
              </Button>
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => sendWhatsApp('followup_nudge')}>
                <RefreshCw className="w-3 h-3 mr-1" /> Follow-up
              </Button>
              <Button size="sm" variant="outline" className="h-8 text-xs" disabled>
                <Phone className="w-3 h-3 mr-1" /> Call (setup)
              </Button>
              <Button size="sm" variant="outline" className="h-8 text-xs" disabled>
                <Mail className="w-3 h-3 mr-1" /> Email (setup)
              </Button>
            </div>

            <div className="border-t border-stone-200 pt-2">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1.5">
                Conversation ({conversation.messages?.length || 0})
              </div>
              {(!conversation.messages || conversation.messages.length === 0) ? (
                <div className="text-xs text-stone-400 italic">No messages yet.</div>
              ) : (
                <div className="space-y-1.5 max-h-[280px] overflow-y-auto hc-scrollbar">
                  {conversation.messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`text-xs p-2 rounded border ${
                        msg.direction === 'outbound'
                          ? 'bg-stone-50 border-stone-200 ml-6'
                          : 'bg-emerald-50 border-emerald-200 mr-6'
                      }`}
                    >
                      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-stone-500">
                        {msg.direction === 'outbound' ? 'Clinic → Patient' : 'Patient → Clinic'}
                        <span className="ml-auto">{msg.status}</span>
                      </div>
                      <div className="mt-0.5">{msg.body || msg.template_name}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Audit tab */}
          <TabsContent value="audit" className="p-3 mt-0">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-2 flex items-center gap-1">
              <History className="w-3 h-3" /> Lifecycle Audit
            </div>
            {history.length === 0 ? (
              <div className="text-xs text-stone-400 italic">No events recorded.</div>
            ) : (
              <ol className="space-y-2 border-l border-stone-200 pl-3">
                {history.map((ev) => {
                  const to = meta(ev.to_state);
                  return (
                    <li key={ev.id} className="text-xs">
                      <div className="text-[10px] text-stone-500 tabular-nums">
                        {new Date(ev.timestamp).toLocaleString()}
                      </div>
                      <div className="font-medium text-stone-800">
                        {ev.from_state ? `${meta(ev.from_state).label} → ` : ''}
                        <span style={{ color: to.text }}>{to.label}</span>
                      </div>
                      <div className="text-stone-500">
                        {ev.actor_name || 'System'}{ev.reason ? ` · ${ev.reason}` : ''}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </TabsContent>
        </ScrollArea>
      </Tabs>
    </aside>
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1">{label}</div>
      <div className="text-sm text-stone-800">{value}</div>
    </div>
  );
}
